<template>
  <div class="filter-selection-container">
    <div class="content">
      <div class="body3 truncate">{{ $t('filter_by') }}:</div>

      <slot name="prepend"></slot>

      <div
        v-if="value.length"
        :style="{
          maxWidth: 'calc(100% - 300px)',
          minWidth: value.length > 1 ? '180px' : '100px',
        }"
      >
        <v-slide-group
          show-arrows
          next-icon="$keyboard_arrow_right"
          prev-icon="$keyboard_arrow_left"
        >
          <v-slide-item v-for="(filter, index) of value" :key="index">
            <other-filter
              :key="`filter-${index}`"
              :parameters="
                parameters.filter((p) => p.value === filter.parameter)
              "
              :selected-parameter="filter.parameter"
              :selected-operation="filter.operation"
              :selected-filters="filter.filters"
              @add:filters="onAddFilters"
            >
              <template #activator="{on: menu, attrs, value}">
                <button
                  class="common-chip mr-1"
                  v-bind="attrs"
                  :style="{
                    color: value ? 'var(--white)' : 'var(--black)',
                    'background-color': value
                      ? 'var(--accent)'
                      : 'var(--white)',
                  }"
                  v-on="menu"
                >
                  <z-icon
                    size="12"
                    :color="value ? 'var(--white)' : 'var(--black)'"
                  >
                    {{
                      parameters.find((p) => p.value === filter.parameter).icon
                    }}
                  </z-icon>

                  <z-tooltip bottom :open-delay="500">
                    <template #activator="{on: tooltip, attrs}">
                      <div v-bind="attrs" v-on="tooltip">
                        {{ filter.filters.map((item) => item.text).join(', ') }}
                      </div>
                    </template>

                    {{ filter.filters.map((item) => item.text).join(', ') }}
                  </z-tooltip>

                  <z-icon
                    size="12"
                    :color="value ? 'var(--white)' : 'var(--black)'"
                    @click.stop="onDeleteFilter(filter)"
                  >
                    $close_rounded
                  </z-icon>
                </button>
              </template>
            </other-filter>
          </v-slide-item>
        </v-slide-group>
      </div>

      <!-- Menu de adicionar filtro -->
      <other-filter
        :parameters="parameters"
        :selected-filters="selectedAddFilters"
        :selected-operation="selectedAddOperation"
        :parameter-type="parameterSelectionType"
        @update:search-input="$emit('update:search-input', $event)"
        @update:menu="onUpdateMenu"
        @update:parameter="onUpdateParameter"
        @add:filters="onAddFilters"
      ></other-filter>

      <div class="append-container">
        <div class="delete-all-filters">
          <z-tooltip>
            <template #activator="{on, attrs}">
              <z-icon
                size="15"
                color="var(--black)"
                v-bind="attrs"
                v-on="on"
                @click="onDeleteAllFilters"
              >
                $delete_rounded
              </z-icon>
            </template>

            {{ $t('remove_all_filters') }}
          </z-tooltip>
        </div>

        <slot name="append"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import OtherFilter from './private/OtherFilter.vue'

export default {
  name: 'ZFilterSelection',
  components: {
    OtherFilter,
  },

  i18n: {
    messages: {
      en: {
        filter_by: 'Filter by',
        remove_all_filters: 'Remove all filters',
      },
      pt: {
        filter_by: 'Filtrar por',
        remove_all_filters: 'Remover todos os filtros',
      },
      es: {
        filter_by: 'Filtrar por',
        remove_all_filters: 'Eliminar todos los filtros',
      },
      fr: {
        filter_by: 'Filtrer par',
        remove_all_filters: 'Supprimer tous les filtres',
      },
      tr: {
        filter_by: 'Filtrele',
        remove_all_filters: 'Tüm filtreleri kaldır',
      },
    },
  },

  props: {
    /**     *
     * @typedef {Object} Filter
     * @property {String} parameter - Parâmetro do filtro (nome do campo)
     * @property {String} operation - Operação do filtro (igual, diferente, etc)
     * @property {Array<{text: string, value: string}>} filters - Lista de filtros selecionados
     */

    /**
     * Lista de filtros selecionados
     * @type {Array<{parameter: string, operation: string, filters:[] }>}
     */
    value: {
      type: Array,
      default: () => [],
    },

    /**
     * Lista de parâmetros disponíveis para filtro
     * @type {Array<{text: string, value: string, icon: string, operations: [], filters: []}>}
     */
    parameters: {
      type: Array,
      required: true,
      default: () => [],
    },

    /**
     * Tipo de parâmetro
     * @type {'select' | 'autocomplete'}
     */
    parameterSelectionType: {
      type: String,
      required: false,
      default: 'autocomplete',
    },
  },

  data() {
    return {
      selectedAddFilters: [],
      selectedAddOperation: '',
    }
  },

  methods: {
    onAddFilters(filter) {
      const selectedFilter = Object.assign(
        {},
        this.value.find((item) => item.parameter === filter.parameter)
      )

      /**
       * se o filtro já existir, atualizamos os dados,
       * caso contrário, adicionamos o filtro
       * */
      if (Object.keys(selectedFilter).length) {
        selectedFilter.filters = filter.filters
        selectedFilter.operation = filter.operation

        this.$emit('input', [
          ...this.value.filter(
            (item) => item.parameter !== selectedFilter.parameter
          ),
          selectedFilter,
        ])
      } else {
        this.$emit('input', [...this.value, filter])
      }
    },

    onDeleteFilter(filter) {
      this.$emit(
        'input',
        this.value.filter((item) => item.parameter !== filter.parameter)
      )
    },

    onDeleteAllFilters() {
      this.$emit('input', [])
    },

    /**
     * Ao selecionar um filtro, atualizamos os dados que permitem que ao abrir o menu de "adicionar filtro",
     */
    onUpdateParameter(parameter) {
      const selectedFilter = this.value.find(
        (filter) => filter.parameter === parameter
      )

      this.selectedAddFilters = selectedFilter?.filters
      this.selectedAddOperation = selectedFilter?.operation
    },

    onUpdateMenu() {
      this.selectedAddFilters = []
      this.selectedAddOperation = ''
    },
  },
}
</script>

<style scoped lang="scss">
@use './common.scss' as *;

.truncate {
  width: auto;
  height: 100%;
  min-width: 50px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-selection-container {
  width: 100%;
  height: 31px;
  max-height: 32px;
  background-color: #fff;
  border-radius: 15px;
  padding: 5px 15px;
  background: var(--black-100);
  min-width: 0 !important;

  .content {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: nowrap;
    gap: 5px;
    width: 100%;

    .append-container {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 5px;
      justify-self: flex-end;
      margin-left: auto;

      .delete-all-filters {
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid var(--black);
        border-radius: 50%;
        width: 21px;
        height: 21px;
      }
    }
  }
}

:deep(.v-slide-group__content) {
  align-items: center;
}

/** 
  Efeito de gradiente nos botões de navegação do slide
*/
:deep(.v-slide-group__prev) {
  position: relative;
  justify-content: center;
  margin-right: 2px;
  min-width: 21px !important;
  max-width: 21px !important;
  max-height: 21px !important;
  border: 1px solid var(--black);
  border-radius: 50%;

  i {
    color: var(--black);
    font-size: 20px;
  }

  &::before {
    content: '';
    position: absolute;
    z-index: 1;
    top: -1px;
    left: 21px;
    pointer-events: none;
    background: linear-gradient(
      60deg,
      #ddd 20.83%,
      rgba(217, 217, 217, 0.2) 100%
    );
    width: 25px;
    height: 21px;
  }

  &.v-slide-group__prev--disabled {
    display: none;
  }
}

:deep(.v-slide-group__next) {
  justify-content: center;
  margin-left: 2px;
  min-width: 21px !important;
  max-width: 21px !important;
  max-height: 21px !important;
  border: 1px solid var(--black);
  border-radius: 50%;

  &::before {
    content: '';
    position: absolute;
    z-index: 1;
    top: 0px;
    right: 21px;
    pointer-events: none;
    background: linear-gradient(
      270deg,
      #ddd 20.83%,
      rgba(217, 217, 217, 0.2) 100%
    );
    width: 25px;
    height: 21px;
  }

  i {
    color: var(--black);
    font-size: 20px;
  }

  &.v-slide-group__next--disabled {
    display: none !important;
  }
}
</style>
